export interface ParsedMDTContext {
  reportText: string;
  disclaimer?: string;
  validation?: {
    status?: string;
    issues?: any[];
    isValid?: boolean;
    warnings?: string[];
  };
  agentReasoning?: {
    histopathology?: any;
    medicalOncology?: any;
    radiology?: any;
    supportiveCare?: any;
  };
  tokenUsage?: {
    prompt?: number;
    completion?: number;
    total_tokens?: number;
  };
  ragCasesUsed?: number;
  executionTime?: number;
  rawObj?: any;
}

export const extractMDTReportContext = (contextVal: any): ParsedMDTContext => {
  if (!contextVal) {
    return { reportText: 'No report content available.' };
  }

  let obj = contextVal;
  if (typeof contextVal === 'string') {
    try {
      obj = JSON.parse(contextVal);
    } catch {
      return { reportText: contextVal };
    }
  }

  if (typeof obj === 'string') {
    return { reportText: obj };
  }

  // Handle Bedrock response structure: { generation, validation, agent_reasoning, disclaimer }
  let reportText = '';
  if (obj.generation && typeof obj.generation === 'string') {
    reportText = obj.generation;
  } else if (obj.mdt_report) {
    if (typeof obj.mdt_report === 'string') {
      try {
        const nested = JSON.parse(obj.mdt_report);
        if (typeof nested === 'object' && nested !== null) {
          if (nested.generation) {
            reportText = nested.generation;
          } else if (nested.consultant_commentary) {
            reportText = `### Patient Summary
- **Name:** ${nested.patient_name || 'N/A'}
- **NHS Number:** ${nested.nhs_number || 'N/A'}
- **Diagnosis:** ${nested.diagnosis || 'N/A'}
- **Stage:** ${nested.stage || 'N/A'}

### Recommendations
${Array.isArray(nested.recommendations) ? nested.recommendations.map((r: string) => `- ${r}`).join('\n') : nested.recommendations || 'N/A'}

### Treatment Plan
${nested.treatment_plan || 'N/A'}

### Consultant Commentary
${nested.consultant_commentary}`;
          } else {
            reportText = obj.mdt_report;
          }
        } else {
          reportText = obj.mdt_report;
        }
      } catch {
        reportText = obj.mdt_report;
      }
    } else if (typeof obj.mdt_report === 'object') {
      return extractMDTReportContext(obj.mdt_report);
    }
  } else {
    // If obj is a plain object with key-values
    reportText = JSON.stringify(obj, null, 2);
  }

  return {
    reportText,
    disclaimer: obj.disclaimer || 'MULTI-AGENT AI REPORT — FOR MDT CLINICAL DISCUSSION ONLY.',
    validation: obj.validation,
    agentReasoning: obj.agent_reasoning || obj.agentReasoning,
    tokenUsage: obj.token_usage || obj.tokenUsage,
    ragCasesUsed: obj.rag_cases_used,
    executionTime: obj.execution_time_seconds,
    rawObj: obj,
  };
};

export const getReportSummarySnippet = (contextVal: any, maxLength: number = 150): string => {
  const parsed = extractMDTReportContext(contextVal);
  const text = parsed.reportText.replace(/[#*`_~|-]/g, ' ').replace(/\s+/g, ' ').trim();
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength) + '...';
};
